import * as React from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Textarea from '@amzn/awsui-components-console/textarea';
import Multiselect, { type MultiselectProps } from '@amzn/awsui-components-console/multiselect';

interface CreateApplicationModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (applicationData: ApplicationData) => void;
}

export interface ApplicationData {
  applicationId: string;
  description: string;
  ownerTeam: string;
  accountId: string;
  processingStrategy: string[];
}

const PROCESSING_STRATEGY_OPTIONS = [
  { label: 'HTML to Markdown', value: 'HTML_TO_MARKDOWN' },
  { label: 'PDF to Markdown', value: 'PDF_TO_MARKDOWN' },
  { label: 'Quip to Markdown', value: 'QUIP_TO_MARKDOWN' },
  { label: 'Quip Table to CSV', value: 'QUIP_TABLE_TO_CSV' },
  { label: 'Quip Table to JsonL', value: 'QUIP_TABLE_TO_JSONL' },
  { label: 'Cover Image Extraction', value: 'COVER_IMAGE_EXTRACTION' },
  { label: 'Title Extraction', value: 'TITLE_EXTRACTION' },
  { label: 'Summary Extraction', value: 'SUMMARY_EXTRACTION' },
  { label: 'Keywords Extraction', value: 'KEYWORDS_EXTRACTION' },
  { label: 'Tags Extraction', value: 'TAGS_EXTRACTION' },
];

const CreateApplicationModal: React.FC<CreateApplicationModalProps> = ({ visible, onDismiss, onConfirm }) => {
  const [applicationId, setApplicationId] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [ownerTeam, setOwnerTeam] = React.useState('');
  const [accountId, setAccountId] = React.useState('');
  const [processingStrategy, setProcessingStrategy] = React.useState<MultiselectProps.Option[]>([]);

  const [applicationIdError, setApplicationIdError] = React.useState('');
  const [descriptionError, setDescriptionError] = React.useState('');
  const [ownerTeamError, setOwnerTeamError] = React.useState('');
  const [accountIdError, setAccountIdError] = React.useState('');

  const validateForm = () => {
    let isValid = true;

    if (!applicationId.trim()) {
      setApplicationIdError('Application ID is required');
      isValid = false;
    } else {
      setApplicationIdError('');
    }

    if (!description.trim()) {
      setDescriptionError('Description is required');
      isValid = false;
    } else {
      setDescriptionError('');
    }

    if (!ownerTeam.trim()) {
      setOwnerTeamError('Owner team is required');
      isValid = false;
    } else {
      setOwnerTeamError('');
    }

    if (!accountId.trim()) {
      setAccountIdError('AWS Account ID is required');
      isValid = false;
    } else if (!/^\d{12}$/.test(accountId.trim())) {
      setAccountIdError('AWS Account ID must be 12 digits');
      isValid = false;
    } else {
      setAccountIdError('');
    }

    return isValid;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm({
        applicationId: applicationId.trim(),
        description: description.trim(),
        ownerTeam: ownerTeam.trim(),
        accountId: accountId.trim(),
        processingStrategy: processingStrategy.map((option) => option.value).filter(Boolean) as string[],
      });

      // Reset form
      setApplicationId('');
      setDescription('');
      setOwnerTeam('');
      setAccountId('');
      setProcessingStrategy([]);
    }
  };

  const handleDismiss = () => {
    // Reset form and errors
    setApplicationId('');
    setDescription('');
    setOwnerTeam('');
    setAccountId('');
    setProcessingStrategy([]);
    setApplicationIdError('');
    setDescriptionError('');
    setOwnerTeamError('');
    setAccountIdError('');
    onDismiss();
  };

  return (
    <Modal
      onDismiss={handleDismiss}
      visible={visible}
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Submit Request
            </Button>
          </SpaceBetween>
        </Box>
      }
      header="Onboard New Application"
    >
      <SpaceBetween size="m">
        <Box>
          Onboard your application to start receiving content from collections. This will create the necessary infrastructure and
          permissions for your application to subscribe content collections.
        </Box>

        <FormField
          label="Application ID"
          errorText={applicationIdError}
          description="Unique identifier for your application (e.g., PathPanda, ContentFlywheel)"
        >
          <Input value={applicationId} onChange={({ detail }) => setApplicationId(detail.value)} placeholder="Enter application ID" />
        </FormField>

        <FormField label="Description" errorText={descriptionError} description="Brief description of your application's purpose">
          <Textarea
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
            placeholder="Describe what your application does"
            rows={3}
          />
        </FormField>

        <FormField label="Owner Team" errorText={ownerTeamError} description="Team responsible for this application">
          <Input
            value={ownerTeam}
            onChange={({ detail }) => setOwnerTeam(detail.value)}
            placeholder="Enter team name (e.g., GCR-MKT-SCE)"
          />
        </FormField>

        <FormField
          label="AWS Account ID"
          errorText={accountIdError}
          description="12-digit AWS account ID where your application resources are deployed"
        >
          <Input value={accountId} onChange={({ detail }) => setAccountId(detail.value)} placeholder="123456789012" />
        </FormField>

        <FormField label="Processing Strategy" description="Select how content should be processed for your application (optional)">
          <Multiselect
            selectedOptions={processingStrategy}
            onChange={({ detail }) => setProcessingStrategy([...detail.selectedOptions])}
            options={PROCESSING_STRATEGY_OPTIONS}
            placeholder="Choose processing strategies"
            filteringType="auto"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default CreateApplicationModal;
